export type RecordingSaveStatus =
  | {
      state: 'saving';
      startedAt: number;
      updatedAt: number;
      expectedFrames?: number;
    }
  | {
      state: 'ready';
      savedAt: number;
      updatedAt: number;
      frames?: number;
    }
  | {
      state: 'failed';
      failedAt: number;
      updatedAt: number;
      error: string;
    };

const KEY_PREFIX = 'bnl-recording-status:';

function storageKey(id: string): string {
  return `${KEY_PREFIX}${id}`;
}

function resolveStorage(storage?: Storage | null): Storage | null {
  if (storage !== undefined) return storage;
  if (typeof window === 'undefined') return null;
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

function readRaw(id: string, storage?: Storage | null): string | null {
  const target = resolveStorage(storage);
  if (!target) return null;
  try {
    return target.getItem(storageKey(id));
  } catch {
    return null;
  }
}

function writeRaw(id: string, value: string, storage?: Storage | null): void {
  const target = resolveStorage(storage);
  if (!target) return;
  try {
    target.setItem(storageKey(id), value);
  } catch {
    // localStorage is only a hint for cross-tab save progress.
  }
}

export function clearRecordingStatus(id: string, storage?: Storage | null): void {
  const target = resolveStorage(storage);
  if (!target) return;
  try {
    target.removeItem(storageKey(id));
  } catch {
    // ignore
  }
}

export function markRecordingSaving(
  id: string,
  details: { expectedFrames?: number } = {},
  storage?: Storage | null,
): void {
  const now = Date.now();
  writeRaw(
    id,
    JSON.stringify({
      state: 'saving',
      startedAt: now,
      updatedAt: now,
      expectedFrames: details.expectedFrames,
    } satisfies RecordingSaveStatus),
    storage,
  );
}

export function markRecordingReady(
  id: string,
  details: { frames?: number } = {},
  storage?: Storage | null,
): void {
  const now = Date.now();
  writeRaw(
    id,
    JSON.stringify({
      state: 'ready',
      savedAt: now,
      updatedAt: now,
      frames: details.frames,
    } satisfies RecordingSaveStatus),
    storage,
  );
}

export function markRecordingFailed(id: string, error: string, storage?: Storage | null): void {
  const now = Date.now();
  writeRaw(
    id,
    JSON.stringify({
      state: 'failed',
      failedAt: now,
      updatedAt: now,
      error,
    } satisfies RecordingSaveStatus),
    storage,
  );
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

export function getRecordingStatus(
  id: string,
  storage?: Storage | null,
): RecordingSaveStatus | null {
  const raw = readRaw(id, storage);
  if (!raw) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    if (
      !isRecord(parsed) ||
      typeof parsed.state !== 'string' ||
      typeof parsed.updatedAt !== 'number'
    ) {
      return null;
    }
    if (parsed.state === 'saving' && typeof parsed.startedAt === 'number') {
      return {
        state: 'saving',
        startedAt: parsed.startedAt,
        updatedAt: parsed.updatedAt,
        expectedFrames:
          typeof parsed.expectedFrames === 'number' ? parsed.expectedFrames : undefined,
      };
    }
    if (parsed.state === 'ready' && typeof parsed.savedAt === 'number') {
      return {
        state: 'ready',
        savedAt: parsed.savedAt,
        updatedAt: parsed.updatedAt,
        frames: typeof parsed.frames === 'number' ? parsed.frames : undefined,
      };
    }
    if (
      parsed.state === 'failed' &&
      typeof parsed.failedAt === 'number' &&
      typeof parsed.error === 'string'
    ) {
      return {
        state: 'failed',
        failedAt: parsed.failedAt,
        updatedAt: parsed.updatedAt,
        error: parsed.error,
      };
    }
  } catch {
    // ignore malformed payloads
  }
  return null;
}
